import { useState } from "react";
import { useTheme } from "../../../context/ThemeContext";
import { useDeals } from "../services/dealsService";
import DealsBoard from "./DealsBoard";
import PipelineBoard from "./PipelineBoard";

// ===========================================================================
// Pipeline — ONE screen, mounted both at Panel CRM → Pipeline and at
// Negocios → Pipeline, so the two are always the same.
//
// Two boards live here: the deals board, which shows the deals in the very
// stages of the HubSpot pipeline, and the lead board (our own categories). With
// deals imported from HubSpot the deals board is the one that opens.
//
// Layout contract: the section fills the content area and never grows wider
// than it (`min-w-0` + `overflow-hidden`); each board scrolls sideways inside.
// ===========================================================================

type PipelineView = "negocios" | "leads";

export default function PipelineSection() {
  const { isDarkMode } = useTheme();
  const deals = useDeals();
  const [chosen, setChosen] = useState<PipelineView | null>(null);
  const hasHubspotDeals = deals.some((d) => d.hubspotStage || (d.source ?? "").toLowerCase() === "hubspot");
  const view: PipelineView = chosen ?? (hasHubspotDeals ? "negocios" : "leads");

  const tab = (id: PipelineView, label: string, icon: string) => (
    <button
      key={id}
      role="tab"
      aria-selected={view === id}
      onClick={() => setChosen(id)}
      className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
        view === id
          ? "bg-primary-500 text-white"
          : isDarkMode
            ? "border border-gray-500 bg-dark-card text-white hover:bg-white/10"
            : "border border-gray-300 bg-white text-gray-900 hover:bg-gray-100"
      }`}
    >
      <i className={icon} />
      {label}
    </button>
  );

  return (
    <div data-testid="pipeline-section" className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden">
      <div role="tablist" aria-label="Tablero" className="flex flex-wrap gap-2 px-6 pt-6">
        {tab("negocios", "Negocios", "ri-briefcase-line")}
        {tab("leads", "Leads", "ri-user-star-line")}
      </div>
      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">{view === "negocios" ? <DealsBoard /> : <PipelineBoard />}</div>
    </div>
  );
}
